import { useCallback, useEffect, useState } from 'react';
import { getLoyaltyDiscountPerformance } from '../services/reportsApi';

/* ═══════════════════════════════════════════════════════════════════════════
   Discount & Promotion Performance (live)
   GET /sales/loyalty/discount-performance?locationId=&startDate=&endDate=&source=&orderSource=

   Feeds the by-source breakdown card inside the Discounts drill-down (Snapshot
   / Sales). `source` is left unset so a single call returns every segment —
   the source pills filter what comes back client-side, which keeps the
   multi-select working and the shares consistent with one total.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Segment identifiers returned by the API. */
export type DiscountPerformanceSource = 'PROMOTIONAL' | 'LOYALTY' | 'MANUAL' | 'THIRD_PARTY';

export interface DiscountPerformanceSegment {
    source: DiscountPerformanceSource | string;
    label: string;
    discountAmount: number;
    percentage: number;
}

export interface DiscountPerformance {
    totalDiscount: number;
    /** True when the Loyalty segment ties out with the Loyalty Report. */
    reconciled: boolean;
    segments: DiscountPerformanceSegment[];
}

export interface UseDiscountPerformanceParams {
    locationId?: string | null;
    /** ISO yyyy-mm-dd. Omit for the Today filter — the backend serves live data. */
    startDate?: string | null;
    /** ISO yyyy-mm-dd. Omit for the Today filter — the backend serves live data. */
    endDate?: string | null;
    /** Global sales-channel filter — omitted for "Overall sales". */
    orderSource?: 'direct' | 'thirdparty';
    /** Optional server-side segment filter; normally left unset. */
    source?: DiscountPerformanceSource;
    token?: string | null;
}

const num = (v: any): number => (Number.isFinite(Number(v)) ? Number(v) : 0);

function normalize(res: any): DiscountPerformance {
    const body = res?.segments ? res : (res?.data ?? res ?? {});
    const segments: DiscountPerformanceSegment[] = Array.isArray(body?.segments)
        ? body.segments.map((s: any) => ({
            source: String(s?.source ?? '').toUpperCase(),
            label: String(s?.label ?? ''),
            discountAmount: num(s?.discountAmount),
            percentage: num(s?.percentage),
        }))
        : [];
    return {
        totalDiscount: num(body?.totalDiscount),
        reconciled: body?.reconciled !== false,
        segments,
    };
}

/* StrictMode mounts every component twice in dev (mount → cleanup → mount), so
   the fetch effect runs twice with identical params and two identical requests
   hit the network — the effect cleanup can only cancel the setState, not the
   request already in flight. Concurrent callers with the same params therefore
   share one in-flight promise. The entry is dropped as soon as the request
   settles, so refetch / a later date change still goes to the network. */
const inFlight = new Map<string, Promise<DiscountPerformance>>();

/** Also used by the card's Export, which refetches on click. */
export function fetchDiscountPerformance(p: UseDiscountPerformanceParams): Promise<DiscountPerformance> {
    const key = JSON.stringify([p.locationId, p.startDate, p.endDate, p.source, p.orderSource, p.token]);
    const pending = inFlight.get(key);
    if (pending) return pending;
    const req = getLoyaltyDiscountPerformance(p)
        .then(normalize)
        .finally(() => { inFlight.delete(key); });
    inFlight.set(key, req);
    return req;
}

export function useDiscountPerformance({
    locationId, startDate, endDate, orderSource, source, token,
}: UseDiscountPerformanceParams) {
    const [data, setData] = useState<DiscountPerformance | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

    useEffect(() => {
        // Dates are optional: omitting both is the Today (live) path.
        if (!locationId) { setData(null); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchDiscountPerformance({ locationId, startDate, endDate, source, orderSource, token })
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err: any) => {
                if (!cancelled) { setError(err?.message || 'Failed to load discount performance'); setData(null); }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [locationId, startDate, endDate, orderSource, source, token, reloadKey]);

    return { data, loading, error, refetch };
}

export default useDiscountPerformance;
